import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import { BRAND } from "../lib/brand";
import { useNight } from "../lib/worldTheme";

function Cloud({ seed, night }) {
  const ref = useRef();
  const data = useMemo(() => {
    const rand = (a, b) => a + Math.random() * (b - a);
    return {
      // spread clouds evenly across the sky using their seed index (5 clouds)
      x: -46 + ((seed + Math.random() * 0.7) / 5) * 92, y: rand(42, 52), z: rand(-42, 42), scale: rand(0.55, 0.95), speed: rand(0.1, 0.26),
      puffs: new Array(Math.floor(rand(3, 5))).fill(0).map(() => ({ p: [rand(-1.4, 1.4), rand(-0.25, 0.3), rand(-0.85, 0.85)], r: rand(0.7, 1.15) })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);
  useFrame((_, delta) => {
    if (!ref.current) return;
    ref.current.position.x += data.speed * delta;
    if (ref.current.position.x > 52) ref.current.position.x = -52;
  });
  const color = night ? "#39405A" : BRAND.white;
  return (
    <group ref={ref} position={[data.x, data.y, data.z]} scale={data.scale}>
      {data.puffs.map((pf, i) => (<mesh key={i} position={pf.p} castShadow={!night}><icosahedronGeometry args={[pf.r, 1]} /><meshStandardMaterial color={color} roughness={1} flatShading /></mesh>))}
    </group>
  );
}

function Bird({ offset, radius, height, speed, dir = 1 }) {
  const ref = useRef();
  const lWing = useRef();
  const rWing = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed + offset;
    const a = t * dir;
    if (ref.current) {
      ref.current.position.x = Math.cos(a) * radius;
      ref.current.position.z = Math.sin(a) * radius * 0.7;
      ref.current.position.y = height + Math.sin(t * 0.7) * 1.2;
      ref.current.rotation.y = -a - Math.PI / 2;
    }
    const flap = Math.sin(state.clock.elapsedTime * 9 + offset) * 0.6;
    if (lWing.current) lWing.current.rotation.z = 0.3 + flap;
    if (rWing.current) rWing.current.rotation.z = -0.3 - flap;
  });
  return (
    <group ref={ref} scale={0.5}>
      <mesh><boxGeometry args={[0.5, 0.16, 0.16]} /><meshStandardMaterial color={BRAND.charcoal} flatShading /></mesh>
      <mesh ref={lWing} position={[0, 0.02, 0.08]}><boxGeometry args={[0.18, 0.04, 0.55]} /><meshStandardMaterial color={BRAND.charcoal} flatShading /></mesh>
      <mesh ref={rWing} position={[0, 0.02, -0.08]}><boxGeometry args={[0.18, 0.04, 0.55]} /><meshStandardMaterial color={BRAND.charcoal} flatShading /></mesh>
    </group>
  );
}

function Moon() {
  return (
    <group position={[-34, 30, -24]}>
      <mesh><sphereGeometry args={[3.2, 24, 24]} /><meshStandardMaterial color={"#E8ECF5"} emissive={"#C9D4EC"} emissiveIntensity={0.9} roughness={1} /></mesh>
      <mesh><sphereGeometry args={[4.4, 20, 20]} /><meshBasicMaterial color={"#AEC0E6"} transparent opacity={0.12} depthWrite={false} /></mesh>
    </group>
  );
}

export default function Sky() {
  const night = useNight();
  const clouds = useMemo(() => new Array(5).fill(0).map((_, i) => i), []);
  const birds = useMemo(() => [
    { offset: 0, radius: 30, height: 22, speed: 0.16, dir: 1 },
    { offset: 0.5, radius: 30.6, height: 22.4, speed: 0.16, dir: 1 },
    { offset: 1.0, radius: 31.2, height: 21.7, speed: 0.16, dir: 1 },
    { offset: 3.0, radius: 24, height: 24, speed: 0.2, dir: -1 },
    { offset: 3.4, radius: 24.6, height: 24.3, speed: 0.2, dir: -1 },
  ], []);
  return (
    <group>
      {clouds.map((s) => (<Cloud key={s} seed={s} night={night} />))}
      {!night && birds.map((b, i) => (<Bird key={i} {...b} />))}
      {night && <Stars radius={150} depth={60} count={1400} factor={4} saturation={0} fade speed={0.4} />}
      {night && <Moon />}
    </group>
  );
}
